import React from 'react';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent
} from '@mui/material';
import { Platform } from '../../../services/api';
import { Account } from '../../../hooks/useAccounts';
import PlatformSelector from '../../common/PlatformSelector';
import AccountSelector from '../../common/AccountSelector';
import ImageUpload from '../../common/ImageUpload';

export interface SimulationFormFieldsProps {
  // Form data
  formData: {
    platform: string;
    accountId: string;
    sector: string;
    content: string;
    imageFile: File | null;
    imagePreviewUrl?: string;
    imageDescription: string;
    simCount: number;
    isPrivate: boolean;
  };
  
  // Form errors
  formErrors: Record<string, string>;
  
  // Data
  platforms: Platform[];
  accounts: Account[];
  
  // Event handlers
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (e: SelectChangeEvent) => void;
  handleImageChange: (file: File | null) => void;
  handlePrivateChange: (checked: boolean) => void;
}

const SimulationFormFields: React.FC<SimulationFormFieldsProps> = ({
  formData,
  formErrors,
  platforms,
  accounts,
  handleInputChange,
  handleSelectChange,
  handleImageChange,
  handlePrivateChange
}) => {
  return (
    <>
      <PlatformSelector
        platforms={platforms}
        value={formData.platform}
        onChange={handleSelectChange}
        error={formErrors.platform}
      />
      
      <AccountSelector
        accounts={accounts}
        value={formData.accountId}
        onChange={handleSelectChange}
        error={formErrors.accountId}
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="sector"
        name="sector"
        label="Sector"
        value={formData.sector}
        onChange={handleInputChange}
        disabled={true}
        helperText="Automatically updated based on selected account"
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="content"
        name="content"
        label="Content Text *"
        multiline
        rows={4}
        value={formData.content}
        onChange={handleInputChange}
        error={!!formErrors.content}
        helperText={formErrors.content || 'Enter the text content of your post'}
      />
      
      <ImageUpload
        value={formData.imageFile}
        previewUrl={formData.imagePreviewUrl}
        onChange={handleImageChange}
      />

      <TextField
        fullWidth
        margin="normal"
        id="imageDescription"
        name="imageDescription"
        label="Media Description (optional)"
        placeholder="Describe the media (image/video/GIF) and notable visual details"
        multiline
        rows={3}
        value={formData.imageDescription || ''}
        onChange={handleInputChange}
        disabled={!!formData.imageFile}
        helperText={formData.imageFile
          ? 'An image file has been uploaded; your media description will not be used.'
          : 'If you don\'t upload media, we\'ll use this description to analyze impact.'}
      />
      
      <TextField
        fullWidth
        margin="normal"
        type="number"
        id="simCount"
        name="simCount"
        label="Number of Simulated Replies"
        value={formData.simCount}
        onChange={handleInputChange}
        inputProps={{ min: 10, max: 50 }}
        error={!!formErrors.simCount}
        helperText={formErrors.simCount || 'Number of simulated replies to generate (10-50)'}
      />
      
      <FormControlLabel
        control={
          <Checkbox
            checked={formData.isPrivate}
            onChange={(e) => handlePrivateChange(e.target.checked)}
            name="isPrivate"
          />
        }
        label="Keep this simulation private (only visible to me)"
        sx={{ mt: 2 }}
      />
    </>
  );
};

export default SimulationFormFields;